<template>
  <div class="ai-tools-page">
    <div class="page-header">
      <h1>AI Tools Catalog</h1>
      <p class="page-description">
        Discover our comprehensive suite of AI-powered tools designed to enhance your business operations and decision-making.
      </p>
    </div>

    <div class="tools-grid">
      <div 
        v-for="tool in aiTools" 
        :key="tool.id" 
        class="tool-card"
        @click="openTool(tool)"
      >
        <div class="tool-icon">
          <i :class="tool.icon"></i>
        </div>
        <div class="tool-content">
          <h3 class="tool-title">{{ tool.name }}</h3>
          <p class="tool-description">{{ tool.description }}</p>
          <div class="tool-tags">
            <span 
              v-for="tag in tool.tags" 
              :key="tag" 
              class="tool-tag"
            >
              {{ tag }}
            </span>
          </div>
        </div>
        <div class="tool-status">
          <span :class="['status-badge', tool.status]">
            {{ tool.statusText }}
          </span>
        </div>
      </div>
    </div>

    <!-- Coming Soon Section -->
    <div class="coming-soon-section">
      <h2>Coming Soon</h2>
      <div class="tools-grid">
        <div 
          v-for="tool in comingSoonTools" 
          :key="tool.id" 
          class="tool-card coming-soon"
        >
          <div class="tool-icon">
            <i :class="tool.icon"></i>
          </div>
          <div class="tool-content">
            <h3 class="tool-title">{{ tool.name }}</h3>
            <p class="tool-description">{{ tool.description }}</p>
            <div class="tool-tags">
              <span 
                v-for="tag in tool.tags" 
                :key="tag" 
                class="tool-tag"
              >
                {{ tag }}
              </span>
            </div>
          </div>
          <div class="tool-status">
            <span class="status-badge coming-soon">
              Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AITools',
  data() {
    return {
      aiTools: [
        {
          id: 1,
          name: 'Competitor Analysis',
          description: 'Analyze competitor strategies, pricing, and market positioning using advanced AI algorithms.',
          icon: 'fas fa-chart-line',
          tags: ['Analytics', 'Market Research', 'Strategy'],
          status: 'available',
          statusText: 'Available'
        },
        {
          id: 2,
          name: 'Product Matching',
          description: 'Intelligent product recommendation engine that matches products based on customer preferences and behavior.',
          icon: 'fas fa-puzzle-piece',
          tags: ['Recommendation', 'E-commerce', 'Personalization'],
          status: 'available',
          statusText: 'Available'
        },
        {
          id: 3,
          name: 'RouteBox Documentation LLM',
          description: 'AI-powered documentation assistant that helps create, update, and maintain technical documentation.',
          icon: 'fas fa-book',
          tags: ['Documentation', 'LLM', 'Technical Writing'],
          status: 'available',
          statusText: 'Available'
        }
      ],
      comingSoonTools: [
        {
          id: 17,
          name: 'Advanced Analytics Dashboard',
          description: 'Comprehensive analytics platform with real-time insights and customizable dashboards.',
          icon: 'fas fa-chart-pie',
          tags: ['Analytics', 'Dashboard', 'Real-time']
        },
        {
          id: 20,
          name: 'Predictive Analytics Suite',
          description: 'Advanced predictive modeling suite for business forecasting and trend analysis.',
          icon: 'fas fa-chart-bar',
          tags: ['Prediction', 'Analytics', 'Modeling']
        }
      ]
    }
  },
  methods: {
    openTool(tool) {
      // For now, just show an alert. In the future, this could navigate to the tool or open a modal
      alert(`Opening ${tool.name}: ${tool.description}`);
    }
  }
}
</script>

<style scoped>
.ai-tools-page {
  padding: 2rem 3rem;
  width: 100%;
  background-color: #f8f9fa;
  min-height: calc(100vh - 48px - 32px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-header h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 700;
}

.page-description {
  font-size: 1.1rem;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  width: 100%;
  flex-shrink: 0;
}

.tool-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e9ecef;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tool-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #3498db;
}

.tool-card.coming-soon {
  opacity: 0.7;
  cursor: not-allowed;
}

.tool-card.coming-soon:hover {
  transform: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border-color: #e9ecef;
}

.tool-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.tool-icon i {
  font-size: 1.5rem;
  color: white;
}

.tool-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tool-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.tool-description {
  color: #6c757d;
  line-height: 1.5;
  margin-bottom: 1rem;
  flex: 1;
}

.tool-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tool-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tool-status {
  margin-top: auto;
}

.status-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.available {
  background: #d4edda;
  color: #155724;
}

.status-badge.coming-soon {
  background: #fff3cd;
  color: #856404;
}

.coming-soon-section {
  margin-top: 4rem;
  margin-bottom: 4rem;
  flex-shrink: 0;
  padding-bottom: 2rem;
}

.coming-soon-section h2 {
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
}


/* Responsive Design */
@media (max-width: 1200px) {
  .tools-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .ai-tools-page {
    padding: 1.5rem 2rem;
    min-height: calc(100vh - 48px - 32px);
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .tools-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .tool-card {
    padding: 1rem;
  }
  
  .tool-icon {
    width: 50px;
    height: 50px;
  }
  
  .tool-icon i {
    font-size: 1.25rem;
  }
}

@media (max-width: 480px) {
  .ai-tools-page {
    padding: 1rem 1.5rem;
    min-height: calc(100vh - 48px - 32px);
  }
  
  .page-header h1 {
    font-size: 1.75rem;
  }
  
  .page-description {
    font-size: 1rem;
  }
  
  .tool-title {
    font-size: 1.1rem;
  }
  
  .tool-description {
    font-size: 0.9rem;
  }
}
</style>
